import { useEffect, useRef, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Reveal straight away - never hide - when the browser cannot watch for scroll
// position or the user has asked for less motion. The animation is an
// enhancement, so content must never end up stuck behind it.
const shouldSkipAnimation = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  if (typeof window.IntersectionObserver === 'undefined') {
    return true;
  }

  return window.matchMedia?.(REDUCED_MOTION_QUERY).matches === true;
};

/**
 * Tracks whether an element has scrolled into view yet.
 *
 * Returns a ref to attach to the element and a flag that flips to true the
 * first time it appears. It never flips back, so content that has already
 * arrived stays put while the user scrolls around it.
 */
const useReveal = ({ threshold = 0, rootMargin = '0px 0px -12% 0px' } = {}) => {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(shouldSkipAnimation);

  useEffect(() => {
    if (isRevealed) {
      return undefined;
    }

    const node = ref.current;
    if (!node) {
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsRevealed(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [isRevealed, threshold, rootMargin]);

  return [ref, isRevealed];
};

export default useReveal;
